import { Component, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { cloneDeep } from 'lodash';

import { CalendarMonthYearHeaderComponent } from 'src/app/components/calendar-month-year-header/calendar-month-year-header.component';
import { TimeParserService } from 'src/app/components/time-date/time-parser.service';
import { environment } from 'src/environments/environment';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { LayoutEditorBase } from '../layout-editor-base/layout-editor.base';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';

@Component({
  selector: 'template-component-layout-counter',
  templateUrl: './layout-counter.component.html',
  styleUrls: ['./layout-counter.component.scss'],
  standalone: false,
})
export class LayoutCounterComponent extends LayoutEditorBase {
  protected readonly layoutComponent = 'rise-layout-counter';

  readonly LayoutType = ComponentsService.LAYOUTS_MAP['rise-layout-counter'];
  static readonly CounterType = {
    DOWN: 'down',
    UP: 'up',
    DAILY: 'daily',
  } as const;

  readonly CounterType = LayoutCounterComponent.CounterType;

  private static readonly DEFAULT_LAYOUT_ID =
    ComponentsService.LAYOUTS_MAP['rise-layout-counter'].SQUARES;
  private static readonly DEFAULT_TEXT_COLOR = 'rgba(255, 255, 255, 1)';
  private static readonly DEFAULT_BACKGROUND_COLOR = 'rgba(12, 29, 56, 0.8)';
  private static readonly DEFAULT_TARGET_TIME = '00:00';

  private readonly counterTypeOrder: string[] = [
    LayoutCounterComponent.CounterType.DOWN,
    LayoutCounterComponent.CounterType.UP,
    LayoutCounterComponent.CounterType.DAILY,
  ];

  private readonly layoutOrder: string[] = [
    ComponentsService.LAYOUTS_MAP['rise-layout-counter'].SQUARES,
    ComponentsService.LAYOUTS_MAP['rise-layout-counter'].CIRCLES,
    ComponentsService.LAYOUTS_MAP['rise-layout-counter'].HORIZONTAL,
  ];

  @ViewChildren('counterTypeBtn')
  counterTypeBtns!: QueryList<ElementRef<HTMLButtonElement>>;

  @ViewChildren('layoutOptionBtn')
  layoutOptionBtns!: QueryList<ElementRef<HTMLButtonElement>>;

  public counterType: string = LayoutCounterComponent.CounterType.DOWN;
  public targetDate: Date | null = null;
  /** Time-of-day value for mat-timepicker (Date; only hours/minutes are used). */
  public targetTime: Date;
  public skipWeekends: boolean = false;
  public completionMessage: string = '';
  override layout = LayoutCounterComponent.DEFAULT_LAYOUT_ID;
  public textColor: string = LayoutCounterComponent.DEFAULT_TEXT_COLOR;
  public backgroundColor: string =
    LayoutCounterComponent.DEFAULT_BACKGROUND_COLOR;
  public showLabels: boolean = true;
  public showHours: boolean = true;
  public showMinutes: boolean = true;
  public showSeconds: boolean = true;

  readonly calendarMonthYearHeader = CalendarMonthYearHeaderComponent;
  readonly matDatepickerConfig = {
    startView: 'month' as const,
    startAt: new Date(),
  };

  private componentServices: unknown[] | null = null;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtils: TemplateEditorUtilsService,
    private timeParserService: TimeParserService,
  ) {
    super();

    this.targetTime = this.timeParserService.midnight();

    this.componentsService.registerDirective({
      type: 'rise-layout-counter',
      element: this.elementRef.nativeElement,
      show: () => {
        const selected = this.componentsService.selected as {
          id: string;
        } | null;
        if (selected?.id) {
          this.componentId = selected.id;
          this.load();
        }
      },
    });
  }

  load() {
    const defaultAttributes =
      this.componentsService.getSelectedDirective()?.defaultAttributes || {};
    const defaultLayout = cloneDeep(defaultAttributes?.layout || {});
    const defaultServices = cloneDeep(defaultAttributes?.services || []);

    const rawLayout =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'layout',
      ) || defaultLayout;
    const rawServices =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'services',
      ) || defaultServices;

    this.componentLayout = this.templateEditorUtils.safeParseJson(
      rawLayout,
      defaultLayout,
    );
    this.componentServices = this.templateEditorUtils.safeParseJson(
      rawServices,
      defaultServices,
    );

    this.loadLayoutSelection(LayoutCounterComponent.DEFAULT_LAYOUT_ID);

    const layoutAttributes =
      (this.componentLayout?.attributes as Record<string, unknown>) || {};
    this.counterType = this.resolveCounterType(
      layoutAttributes.counterType as string | undefined,
    );
    this.textColor =
      (layoutAttributes.textColor as string) ??
      LayoutCounterComponent.DEFAULT_TEXT_COLOR;
    this.backgroundColor =
      (layoutAttributes.backgroundColor as string) ??
      LayoutCounterComponent.DEFAULT_BACKGROUND_COLOR;
    this.showLabels =
      layoutAttributes.showLabels !== undefined
        ? !!layoutAttributes.showLabels
        : true;
    this.showHours =
      layoutAttributes.showHours !== undefined
        ? !!layoutAttributes.showHours
        : true;
    this.showMinutes =
      layoutAttributes.showMinutes !== undefined
        ? !!layoutAttributes.showMinutes
        : true;
    this.showSeconds =
      layoutAttributes.showSeconds !== undefined
        ? !!layoutAttributes.showSeconds
        : true;

    const serviceAttributes = this.getFirstServiceAttributes();
    this.loadTargetDateTime(
      serviceAttributes.date as string | null | undefined,
      serviceAttributes.time as string | null | undefined,
    );
    this.completionMessage =
      typeof serviceAttributes.completion === 'string'
        ? serviceAttributes.completion
        : '';
    this.skipWeekends =
      serviceAttributes.skipWeekends !== undefined
        ? !!serviceAttributes.skipWeekends
        : false;

    // If environment is not defined, this is a new component — persist defaults.
    const env = this.attributeDataService.getAttributeData(
      this.componentId,
      'environment',
    );
    if (!env) {
      if (
        !this.targetDate &&
        this.counterType !== LayoutCounterComponent.CounterType.DAILY
      ) {
        const thirtyDaysOut = new Date();
        thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);
        thirtyDaysOut.setHours(0, 0, 0, 0);
        this.targetDate = thirtyDaysOut;
      }
      this.save();
    }
  }

  save() {
    this.applyLayoutSelection();

    const existingLayoutAttributes =
      (this.componentLayout.attributes as Record<string, unknown>) || {};
    this.componentLayout.attributes = {
      ...existingLayoutAttributes,
      counterType: this.counterType,
      textColor: this.textColor,
      backgroundColor: this.backgroundColor,
      showLabels: this.showLabels,
      // Daily always shows hours at runtime; keep editor preference in memory.
      showHours: this.showHoursForSave(),
      showMinutes: this.showMinutes,
      showSeconds: this.showSeconds,
      blockCount: this.blockCountForSave(),
    };

    if (
      !Array.isArray(this.componentServices) ||
      this.componentServices.length === 0
    ) {
      this.componentServices = [{}];
    }

    const firstService = this.componentServices[0] as Record<string, unknown>;
    if (!firstService.type) {
      firstService.type = 'component';
    }
    if (!firstService.source) {
      firstService.source = 'rise-data-counter';
    }

    const existingServiceAttributes =
      (firstService.attributes as Record<string, unknown>) || {};
    const serviceAttributes: Record<string, unknown> = {
      ...existingServiceAttributes,
      // rise-data-counter only accepts "down" | "up"; UI "daily" maps to "down".
      type: this.serviceTypeFor(this.counterType),
      // Used by rise-data-counter only when type is "down" (includes UI "daily").
      completion: this.completionMessage || '',
      // Daily ignores skip weekends at runtime; keep editor preference in memory.
      skipWeekends: this.skipWeekendsForSave(),
    };
    this.applyServiceDateTime(serviceAttributes);
    firstService.attributes = serviceAttributes;

    this.attributeDataService.setAttributeData(
      this.componentId,
      'layout',
      this.componentLayout,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'services',
      this.componentServices,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }

  /**
   * Maps the editor counter type to the value rise-data-counter accepts.
   * "daily" is a UI-only mode that counts down to a time every day.
   */
  private serviceTypeFor(counterType: string): string {
    return counterType === LayoutCounterComponent.CounterType.UP
      ? LayoutCounterComponent.CounterType.UP
      : LayoutCounterComponent.CounterType.DOWN;
  }

  private resolveCounterType(stored: string | undefined): string {
    if (stored && this.counterTypeOrder.includes(stored)) {
      return stored;
    }
    return LayoutCounterComponent.CounterType.DOWN;
  }

  private getFirstServiceAttributes(): Record<string, unknown> {
    if (
      !Array.isArray(this.componentServices) ||
      this.componentServices.length === 0
    ) {
      return {};
    }
    const firstService = this.componentServices[0] as Record<string, unknown>;
    return (firstService.attributes as Record<string, unknown>) || {};
  }

  private loadTargetDateTime(
    storedDate: string | null | undefined,
    storedTime: string | null | undefined,
  ): void {
    if (storedDate) {
      const localDate = new Date(storedDate);
      localDate.setMinutes(
        localDate.getMinutes() + localDate.getTimezoneOffset(),
      );
      this.targetDate = localDate;
    } else {
      this.targetDate = null;
    }

    const parsedTime = storedTime
      ? this.timeParserService.parseMilitary(storedTime)
      : null;
    this.targetTime = parsedTime || this.timeParserService.midnight();
  }

  /**
   * Writes date/time for rise-data-counter.
   * - daily: date null + time (intentional time-only path)
   * - down/up with a target date: date + time
   * - down/up without a target date: omit both so we never publish
   *   (!date && time), which Lit treats as every-day / daily
   */
  private applyServiceDateTime(
    serviceAttributes: Record<string, unknown>,
  ): void {
    if (this.counterType === LayoutCounterComponent.CounterType.DAILY) {
      serviceAttributes.date = null;
      serviceAttributes.time = this.timeForService();
      return;
    }

    if (this.targetDate) {
      serviceAttributes.date = this.dateForService();
      serviceAttributes.time = this.timeForService();
      return;
    }

    delete serviceAttributes.date;
    delete serviceAttributes.time;
  }

  /** rise-data-counter expects YYYY-MM-DD. Caller must ensure targetDate is set. */
  private dateForService(): string {
    const localDate = new Date(this.targetDate as Date);
    localDate.setMinutes(
      localDate.getMinutes() - localDate.getTimezoneOffset(),
    );
    return this.templateEditorUtils.formatISODate(localDate);
  }

  /** rise-data-counter expects HH:mm. */
  private timeForService(): string {
    if (!this.targetTime) {
      return LayoutCounterComponent.DEFAULT_TARGET_TIME;
    }
    return this.timeParserService.parseDateMilitary(this.targetTime);
  }

  setCounterType(type: string): void {
    if (type === this.counterType) return;
    this.counterType = type;
    if (type === LayoutCounterComponent.CounterType.DAILY) {
      this.targetDate = null;
    } else if (
      type === LayoutCounterComponent.CounterType.UP &&
      (!this.targetDate || this.targetDate > new Date())
    ) {
      const thirtyDaysBack = new Date();
      thirtyDaysBack.setDate(thirtyDaysBack.getDate() - 30);
      thirtyDaysBack.setHours(0, 0, 0, 0);
      this.targetDate = thirtyDaysBack;
    } else if (type === LayoutCounterComponent.CounterType.DOWN) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      if (!this.targetDate || this.targetDate < startOfToday) {
        const thirtyDaysOut = new Date();
        thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);
        thirtyDaysOut.setHours(0, 0, 0, 0);
        this.targetDate = thirtyDaysOut;
      }
    }
    this.save();
  }

  /** Daily always shows hours; do not mutate the editor preference. */
  private showHoursForSave(): boolean {
    return this.counterType === LayoutCounterComponent.CounterType.DAILY
      ? true
      : this.showHours;
  }

  /** Daily does not skip weekends; do not mutate the editor preference. */
  private skipWeekendsForSave(): boolean {
    return this.counterType === LayoutCounterComponent.CounterType.DAILY
      ? false
      : this.skipWeekends;
  }

  // Returns the number of blocks that will be visible at runtime.
  private blockCountForSave(): number {
    const isDaily =
      this.counterType === LayoutCounterComponent.CounterType.DAILY;
    return (
      (isDaily ? 0 : 1) + // DAYS block only for non-daily
      (this.showHoursForSave() ? 1 : 0) +
      (this.showMinutes ? 1 : 0) +
      (this.showSeconds ? 1 : 0)
    );
  }

  setSkipWeekends(checked: boolean): void {
    this.skipWeekends = checked;
    this.save();
  }

  setShowLabels(checked: boolean): void {
    this.showLabels = checked;
    this.save();
  }

  /**
   * Hours > minutes > seconds: turning a unit off also turns off smaller units;
   * turning a unit on also turns on larger units. Labels are independent.
   */
  setShowHours(checked: boolean): void {
    this.showHours = checked;
    if (!checked) {
      this.showMinutes = false;
      this.showSeconds = false;
    }
    this.save();
  }

  setShowMinutes(checked: boolean): void {
    this.showMinutes = checked;
    if (!checked) {
      this.showSeconds = false;
    } else {
      this.showHours = true;
    }
    this.save();
  }

  setShowSeconds(checked: boolean): void {
    this.showSeconds = checked;
    if (checked) {
      this.showMinutes = true;
      this.showHours = true;
    }
    this.save();
  }

  /**
   * Handles keyboard navigation within the counter type radiogroup.
   * Implements the ARIA radiogroup pattern: arrow keys move focus and
   * selection, only the selected option is in the tab sequence.
   */
  onCounterTypeKeydown(event: KeyboardEvent, type: string): void {
    const idx = this.counterTypeOrder.indexOf(type);
    let nextIdx: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (idx + 1) % this.counterTypeOrder.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx =
          (idx - 1 + this.counterTypeOrder.length) %
          this.counterTypeOrder.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = this.counterTypeOrder.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setCounterType(this.counterTypeOrder[nextIdx]);
    this.counterTypeBtns.get(nextIdx)?.nativeElement.focus();
  }

  setLayout(layoutId: string): void {
    this.layout = layoutId;
    this.save();
  }

  /**
   * Handles keyboard navigation within the layout radiogroup.
   * Implements the ARIA radiogroup pattern: arrow keys move focus and
   * selection, only the selected option is in the tab sequence.
   */
  onLayoutKeydown(event: KeyboardEvent, layoutId: string): void {
    const idx = this.layoutOrder.indexOf(layoutId);
    let nextIdx: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (idx + 1) % this.layoutOrder.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx = (idx - 1 + this.layoutOrder.length) % this.layoutOrder.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = this.layoutOrder.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setLayout(this.layoutOrder[nextIdx]);
    this.layoutOptionBtns.get(nextIdx)?.nativeElement.focus();
  }
}
